import { useCallback, useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Focusables visibles dentro del panel, en orden de tab. */
function getFocusable(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.getClientRects().length > 0,
  );
}

/**
 * A11y de diálogos/overlays modales, sin dependencias.
 *
 * Al montar (o al pasar `active` a true):
 *  - guarda el elemento que tenía el foco y lo restaura al desmontar,
 *  - enfoca el primer focusable del panel (o el panel mismo),
 *  - cierra con Escape,
 *  - **atrapa el foco** (Tab / Shift+Tab ciclan dentro del panel; si el foco
 *    escapa, lo devuelve al primer focusable).
 *
 * Uso:
 *   const panelRef = useDialogA11y(onClose);
 *   <div role="dialog" aria-modal="true"><div ref={panelRef}>…</div></div>
 *
 * @param onClose  callback de cierre (Escape).
 * @param active   si el diálogo está activo (default true).
 * @returns ref (callback) para el panel interno del diálogo.
 */
export function useDialogA11y(
  onClose: () => void,
  active = true,
): (node: HTMLElement | null) => void {
  const panelRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const setPanel = useCallback((node: HTMLElement | null) => {
    panelRef.current = node;
  }, []);

  useEffect(() => {
    if (!active) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Foco inicial: primer focusable del panel, o el panel mismo.
    const panel = panelRef.current;
    if (panel) {
      const first = getFocusable(panel)[0];
      (first ?? panel).focus?.();
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const p = panelRef.current;
      if (!p) return;
      const focusables = getFocusable(p);
      if (focusables.length === 0) {
        // Nada focusable dentro: mantener el foco en el panel.
        e.preventDefault();
        p.focus?.();
        return;
      }
      const firstEl = focusables[0]!;
      const lastEl = focusables[focusables.length - 1]!;
      const activeEl = document.activeElement as HTMLElement | null;

      // Foco fuera del panel → tráelo dentro.
      if (!p.contains(activeEl)) {
        e.preventDefault();
        firstEl.focus();
        return;
      }
      // Ciclado en los extremos.
      if (e.shiftKey && activeEl === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && activeEl === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [active]);

  return setPanel;
}
